import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type OpsXrefRow } from '../lib/api';
import { link } from '../lib/deepLink';
import { flagsOf } from '../pages/OpsTransit';
import Modal from './Modal';
import { dash, money } from './DataTable';

/** 출납 줄이 OPS 대중교통비·선지급과 이어져 있을 수 있는가 — 자동 줄이거나 적요에 DY 번호(서버 `dyDigitsIn` 과 같은 모양). */
export const hasOpsRef = (source: string | null | undefined, summary: string | null | undefined) =>
  source === 'ops' || /DY\d{6,}/.test(String(summary ?? ''));

const chip = 'rounded border border-line px-1.5 py-0.5 text-xs text-brand-700 hover:bg-brand-50';

/**
 * 출납일보 줄의 작은 「DY」 뱃지(2026-10-02 way — 월보구 증빙: 출납 줄에서 한 번에 그 영업정산 줄로).
 * 누르면 같은 DY 번호의 OPS 연결·영업정산 줄을 띄운다(`opsTransitXref` · 눌렀을 때만 읽는다 — 3,000줄 표에 미리 붙이지 않는다).
 * 🔴 줄의 끌어 고르기·두 번 누르기·칸 복사를 건드리지 않게 마우스 이벤트를 여기서 멈춘다.
 */
export default function OpsCashBadge({ cashBookId, source }: { cashBookId: number | string; source: string }) {
  const [open, setOpen] = useState(false);
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  return (
    <>
      <button type="button" onMouseDown={stop} onDoubleClick={stop}
              onClick={(e) => { e.stopPropagation(); setOpen(true); }}
              className={`shrink-0 rounded px-1 text-[10px] font-bold ${source === 'ops' ? 'bg-violet-100 text-violet-800' : 'bg-sky-100 text-sky-800'}`}
              title="OPS 대중교통비·선지급 — 눌러서 영업정산 줄·OPS 연결 보기">
        DY
      </button>
      {open && (
        <Modal width="max-w-2xl">
          <div onMouseDown={stop} onDoubleClick={stop} onClick={stop}>
            <OpsCashView cashBookId={cashBookId} onClose={() => setOpen(false)} />
          </div>
        </Modal>
      )}
    </>
  );
}

function OpsCashView({ cashBookId, onClose }: { cashBookId: number | string; onClose: () => void }) {
  const [rows, setRows] = useState<OpsXrefRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    api.opsTransitXref({ cashBookId: String(cashBookId) })
      .then((r) => { if (alive) setRows(r.rows); })
      .catch((e) => { if (alive) setErr(e instanceof Error ? e.message : String(e)); });
    return () => { alive = false; };
  }, [cashBookId]);
  return (
    <div className="card space-y-3 p-4 text-sm">
      <div className="flex items-center gap-2">
        <h2 className="text-base font-bold">출납 줄 → OPS · 영업정산</h2>
        <button className="btn-ghost ml-auto" onClick={onClose}>닫기</button>
      </div>
      {err && <p className="text-red-700">{err}</p>}
      {!rows && !err && <p className="text-ink-faint">불러오는 중…</p>}
      {rows && rows.length === 0 && <p className="text-ink-faint">이 줄에 이어진 DY 번호가 없습니다.</p>}
      {rows?.map((x) => {
        const f = flagsOf(x);
        const reqWhy = x.req_lines.length ? null
          : !x.doc_id ? 'DYOPS 연결 없음(자동 반영 시작 전 손입력이거나 다른 정산년월) — 영업정산 비고에도 이 번호가 없습니다'
          : x.req_error ?? (x.req_mode === 'removed' ? '사람이 지움' : !x.paid_date ? '지급 체크 전 — 영업정산은 회계 지급 뒤에 들어갑니다' : '아직 반영 전(10분마다)');
        return (
          <section key={x.dy_no} className="space-y-1.5 rounded-lg border border-line p-3">
            <div className="flex flex-wrap items-center gap-2">
              <b className="font-mono">{x.dy_no}</b>
              {x.doc_id && <span className="text-xs text-ink-soft">{x.kind === 'advance' ? '선지급' : '대중교통비'} {dash(x.burden)} · {dash(x.settle_ym)} · {dash(x.payee)} · {money(x.amount)}</span>}
              <Link className={`${chip} ml-auto`} to={link.opsTransit(x.dy_no)} onClick={onClose}>OPS 탭에서 보기</Link>
            </div>
            <div className="text-xs">
              <div className="mb-0.5 text-ink-soft">영업정산</div>
              {x.req_lines.map((q) => (
                <div key={q.id} className="flex flex-wrap items-center gap-2">
                  <Link className={chip} to={link.resultReq(q.id, q.mon)} onClick={onClose}>
                    {q.mon} · 접수 {dash(q.recv)} · {dash(q.account)} {money(q.amount)} →
                  </Link>
                  {q.linked ? <span className="text-ink-faint">자동 반영 줄</span> : <span className="text-ink-faint">비고에 같은 번호({dash(q.by)})</span>}
                  {q.held && <span className="font-medium text-amber-800">청구 대기 — 명세 미반영(미수 {q.misuState ?? '등록 없음'})</span>}
                </div>
              ))}
              {reqWhy && <div className="font-medium text-red-700">영업정산 미반영 — {reqWhy}</div>}
            </div>
            {x.cash_lines.length > 1 && (
              <div className="text-xs">
                <div className="mb-0.5 text-ink-soft">같은 번호의 출납 줄</div>
                {x.cash_lines.map((c) => (
                  <Link key={c.id} className={`${chip} mr-1`} to={link.cashBook(c.id, c.date)} onClick={onClose}>
                    {c.date} {c.corp ?? ''} {c.source === 'ops' ? '자동' : '손입력'} {money(c.amount)}{c.deducted ? ` · 재무추가 NO ${c.deducted}` : ''}
                  </Link>
                ))}
              </div>
            )}
            {f.dup.map((d) => <div key={d} className="text-xs font-bold text-red-700">⚠ 중복: {d}</div>)}
          </section>
        );
      })}
    </div>
  );
}
